import React, { useEffect, useState } from 'react';
import { ClipLoader } from 'react-spinners';
import useApi from '../../../../../utils/AxiosWrapper';
import { useScreenView } from '../../../../../utils/ScreenViewContext';
import { useQuery } from '@tanstack/react-query';
import { useUser } from '../../../../../utils/UserContext';
import dayjs from 'dayjs';
import { OdometerHistoryTable } from './widgets/asset-details/OdometerHistoryTable';
import { DatePicker, Space } from 'antd';
import { useParams } from 'react-router-dom';

interface OdometerHistoryProps {
  userType: string;
  currentPage: string;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  subTab: string;
  setSubTab: (tab: string) => void;
}

export interface IOdometerHistory {
  snapshot_id: string;
  cumulative_odometer: number;
  truck_id: string;
  created_at: string;
  day_interval: number;
  driver?: {
    first_name: string;
    last_name: string;
  };
  manually_completed_at?: string;
  miles?: number;
  requested_driver_id?: string;
  source: string;
  status: string;
  submitted_by_driver_id?: string;
  submitted_by_type?: string;
  previous_odometer?: number | null;
  truck: {
    truck_display_name: string;
    truck_make: string;
    truck_model: string;
    truck_year: number;
    vehicle_id_number: string;
    current_mileage?: number | null;
  };
}

export interface IOdometerHistoryResponse {
  odometerHistory: IOdometerHistory[];
  pagination?: {
    totalCount: number;
    hasMore: boolean;
  };
}

export interface RecentMileageSnapshots {
  created_at: string;
  cumulative_odometer: number;
  day_interval: number;
  manually_completed_at: string | null;
  miles: number | null;
  requested_driver_id: string | null;
  snapshot_id: string | null;
  source: string | null;
  status: string | null;
  submitted_by_driver_id: string | null;
  submitted_by_type: string | null;
  truck_id: string | null;
}

export const OdometerHistory = ({
  userType,
  currentPage,
  currentTab,
  setCurrentTab,
  subTab,
  setSubTab,
}: OdometerHistoryProps) => {
  const { screenView } = useScreenView();
  const { callProtectedApi } = useApi();
  // default to [5 years ago, today] to show full odometer history
  const defaultStart = dayjs().subtract(5, 'year').startOf('year');
  const defaultEnd = dayjs().endOf('day');
  const { truckId } = useParams<{ truckId: string }>();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [perPage, setPerPage] = useState(20);
  const [startDate, setStartDate] = useState<string>(
    defaultStart.toISOString()
  );
  const [endDate, setEndDate] = useState<string>(defaultEnd.toISOString());
  const { RangePicker } = DatePicker;

  const { userId, userRecord } = useUser();

  useEffect(() => {
    setPage(1);
    setPageSize(20);
    setPerPage(20);
  }, []);

  const handleDateChange = (
    dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null
  ) => {
    if (dates && dates[0] && dates[1]) {
      setStartDate(dates[0].startOf('day').toISOString());
      setEndDate(dates[1].endOf('day').toISOString());
      setPage(1);
    } else {
      setStartDate(defaultStart.toISOString());
      setEndDate(defaultEnd.toISOString());
      setPage(1);
    }
  };

  const disabledDate = (current: dayjs.Dayjs) =>
    current && current > dayjs().endOf('day');

  const normalizeMileageResponse = (
    raw: any
  ): { items: IOdometerHistory[]; total: number } => {
    // Prefer the new explicit contract if present
    const explicit: IOdometerHistoryResponse | undefined = raw?.odometerHistory
      ? raw
      : raw?.data?.odometerHistory
        ? raw.data
        : undefined;

    if (explicit) {
      const items = explicit.odometerHistory ?? [];
      const total = explicit.pagination?.totalCount ?? items.length;
      return { items, total };
    }

    // Fallbacks (some backends keep nesting)
    const itemsFromCommonNesting =
      raw?.data?.data?.mileageReportRequests ??
      raw?.data?.mileageReportRequests ??
      raw?.mileageReportRequests ??
      [];

    // If customer and backend returns a paginated envelope but different nesting
    const totalGuess =
      raw?.data?.data?.pagination?.totalCount ??
      raw?.pagination?.totalCount ??
      itemsFromCommonNesting.length;

    return { items: itemsFromCommonNesting, total: totalGuess };
  };

  const fetchOdometerHistory = async (
    truck_id: string,
    startDate: string,
    endDate: string
  ) => {
    const response = await callProtectedApi(
      `/electronicLoggingDevices/reports/${userRecord?.userType}/${userId}?startDate=${startDate}&endDate=${endDate}&truck_id=${truck_id}&status=submitted&page=${page}&pageSize=${pageSize}`,
      'get',
      {}
    );

    const raw = response?.data?.data ?? response?.data ?? response;
    return normalizeMileageResponse(raw);
  };

  const { data: odometerHistoryQueryData, isLoading: loadingMileageReports } =
    useQuery({
      queryKey: [
        'odometerHistory',
        truckId ?? '',
        startDate,
        endDate,
        userId,
        page,
        pageSize,
      ],
      queryFn: async () =>
        fetchOdometerHistory(
          truckId || '',
          startDate?.split('T')[0],
          endDate?.split('T')[0]
        ),
      enabled: !!userId && !!truckId,
      staleTime: 15 * 60 * 1000,
      gcTime: 15 * 60 * 1000,
    });

  // Handlers
  const handlePerPageChange = (value: number) => {
    setPerPage(value);
    setPage(1);
    setPageSize(value);
  };

  const handlePageChange = (nextPage: number, nextPageSize: number) => {
    if (nextPageSize !== pageSize) setPageSize(nextPageSize);
    setPage(nextPage);
  };

  const odometerHistory = odometerHistoryQueryData?.items ?? [];
  const odometerHistoryCount = odometerHistoryQueryData?.total ?? 0;

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div className="flex h-full w-full max-w-full flex-col space-y-4 overflow-x-hidden py-[16px] max-lg:px-4 lg:w-[1148px] lg:max-w-[1148px] lg:pt-[16px]">
            <Space
              className="h-[48px] overflow-visible"
              direction="vertical"
              size={12}
            >
              <RangePicker
                onChange={handleDateChange}
                disabledDate={disabledDate}
                className="h-full min-h-[48px] overflow-hidden"
                value={[
                  startDate ? dayjs(startDate) : null,
                  endDate ? dayjs(endDate) : null,
                ]}
                allowClear
              />
            </Space>
            {!loadingMileageReports ? (
              <OdometerHistoryTable
                odometerHistory={odometerHistory}
                offset={(page - 1) * pageSize}
                limit={pageSize}
                perPage={perPage}
                odometerHistoryCount={odometerHistoryCount}
                handlePerPageChange={handlePerPageChange}
                handlePageChange={handlePageChange}
              />
            ) : (
              <ClipLoader
                color="#1D213B"
                loading={loadingMileageReports}
                size={50}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="mt-10 flex h-full w-full items-center justify-center"
              />
            )}
          </div>
        );
    }
  };

  return <>{renderScreenView(screenView)}</>;
};

export default OdometerHistory;
